/**
 * Gestion des redirections d'URL pour préserver le SEO
 */

import { supabase } from '../lib/supabaseClient';
import { logger } from './logger';

export interface URLRedirect {
  id: string;
  old_path: string;
  new_path: string;
  redirect_type: number;
  reason: string | null;
  is_active: boolean;
  hit_count: number;
}

/**
 * Cherche une redirection pour un chemin donné
 */
export async function findRedirect(oldPath: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('url_redirects')
      .select('new_path, id')
      .eq('old_path', oldPath)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching redirect:', error);
      return null;
    }

    if (data) {
      // Incrémenter le compteur de hits en arrière-plan (fire and forget)
      supabase
        .rpc('increment_redirect_hit_count', { redirect_id: data.id })
        .then(() => {})
        .catch((err) => logger.warn('Failed to increment hit count:', err));

      return data.new_path;
    }

    return null;
  } catch (err) {
    logger.error('Exception in findRedirect:', err);
    return null;
  }
}

/**
 * Détecte si un chemin correspond à un pattern d'URL obsolète
 * et génère automatiquement une redirection si possible
 */
export function detectObsoleteUrlPattern(path: string): string | null {
  // Pattern pour détecter le préfixe singulier /cotation/ au lieu de /cotations/
  const singularPattern = /^\/cotation\/(.+)$/;
  const singularMatch = path.match(singularPattern);

  if (singularMatch) {
    const [, rest] = singularMatch;
    // Convertir les points en tirets dans le reste du chemin
    const normalizedRest = rest.replace(/\./g, '-');

    // Rediriger vers la version plurielle avec tirets
    return `/cotations/${normalizedRest}`;
  }

  // Pattern pour détecter les URLs july-2025 qui n'existent pas
  const july2025Pattern = /^\/cotations\/([a-z]{2,4})-(\d+(?:-\d+)?)-july-2025$/;
  const match = path.match(july2025Pattern);

  if (match) {
    const [, letterKey, cotationValue] = match;

    // Rediriger vers la version actuelle (sans suffixe de date)
    return `/cotations/${letterKey}-${cotationValue}`;
  }

  // Pattern pour détecter les URLs avec des formats invalides de dates
  const invalidDatePattern = /^\/cotations\/([a-z]{2,4})-(\d+)-(\d+)-july-(\d{4})$/;
  const invalidMatch = path.match(invalidDatePattern);

  if (invalidMatch) {
    const [, letterKey, , cotationValue, year] = invalidMatch;

    // Si c'est une année valide (2026 ou 2027), rediriger vers le format correct
    if (year === '2026' || year === '2027') {
      return `/cotations/${letterKey}-${cotationValue}-july-${year}`;
    }

    // Sinon rediriger vers la version actuelle
    return `/cotations/${letterKey}-${cotationValue}`;
  }

  return null;
}

/**
 * Hook React pour gérer les redirections automatiques
 * À utiliser dans le composant racine de l'application
 */
export function useUrlRedirects(location: { pathname: string }, navigate: (path: string, options?: any) => void) {
  // Vérifier d'abord les patterns obsolètes connus
  const autoRedirect = detectObsoleteUrlPattern(location.pathname);

  if (autoRedirect) {
    logger.info(`Auto-redirecting from ${location.pathname} to ${autoRedirect}`);
    navigate(autoRedirect, { replace: true });
    return;
  }

  // Sinon, vérifier dans la base de données
  findRedirect(location.pathname).then((newPath) => {
    if (newPath && newPath !== location.pathname) {
      logger.info(`Database redirect from ${location.pathname} to ${newPath}`);
      navigate(newPath, { replace: true });
    }
  });
}
